import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { pageSeo } from '@/lib/seo'
import { msg } from '@lingui/core/macro'
import { useSessionUser } from '@/hooks/use-session'
import { useState } from 'react'
import { fileToBase64 } from '@/components/custom/form'
import { useAppForm } from '@/lib/use-app-form'
import { usePostLoginRedirect } from '@/hooks/use-post-login-redirect'
import { withViewTransition } from '@/lib/utils'
import { useMutation } from '@tanstack/react-query'
import * as v from 'valibot'
import { useLingui } from '@lingui/react/macro'
import { useAppStore } from '@/lib/app-store'
import { useLists } from '@/hooks/use-lists'
import { trpc, type User } from '@/main'
import {
  companyDetailsSchema,
  companyLocationSchema,
  contactInfoSchema,
  firstIncompleteCompanyStep,
} from 'shared/company-form'
import { CompanyRegisterForm } from '@/routes/_app/-components/companies/register-form'
import { AuthCardSkeleton } from '@/routes/_app/-components/app-layout-skeleton'
import { Button } from '@/components/ui/button'
import { FlowFrame } from '@/routes/_app/-components/shared/flow-frame'
import { AuthLayout } from '@/routes/_app/-components/auth/auth-layout'
import { CheckIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'
import { HugeiconsIcon } from '@hugeicons/react'
import { CameraAddIcon } from '@hugeicons/core-free-icons'
import { revalidateLogic } from '@tanstack/react-form'
import type { CompanyForm } from 'shared'
import type { Callback } from 'shared/types'

export const Route = createFileRoute('/_public/register/company')({
  validateSearch: v.object({
    redirect: v.optional(v.string()),
    step: v.fallback(v.optional(v.picklist(['details', 'location', 'contact'])), undefined),
  }),
  head: (ctx) =>
    pageSeo(ctx, {
      path: '/register/company',
      title: msg`Sign up your company`,
      description: msg`Try D’Anchiano free for 14 days: assess candidates, employees, positions and teams with psychographic data.`,
    }),
  component: RouteComponent,
  pendingComponent: () => <AuthCardSkeleton logo social terms />,
})

function RouteComponent() {
  const initialData = useAppStore((state) => state.store.companyData)
  const callback = useAppStore((state) => state.store.callback)
  const user = useSessionUser()
  const { step } = Route.useSearch()

  if (!user) {
    return <CompanyRegisterForm />
  }

  // Resume at the requested step, otherwise the first step with required fields still missing.
  const initialStep = step ?? firstIncompleteCompanyStep(user) ?? undefined

  return (
    <AuthLayout>
      <div className="mx-auto flex w-full max-w-[480px] flex-col gap-5 px-4 py-6">
        <CompanyFormSection user={user} initialData={initialData} callback={callback} initialStep={initialStep} />
      </div>
    </AuthLayout>
  )
}

// Mirrors the applicant profile wizard: a stepped flow (details → location → contact) where the
// FlowFrame box morphs between steps (flow-frame/flow-content) and each step has its own header and
// Back/Continue navigation. See ApplicantFormSection for the shared design.
const COMPANY_STEPS = ['details', 'location', 'contact'] as const

function CompanyFormSection(props: {
  user: User
  initialData?: Partial<CompanyForm>
  callback?: (args: Callback) => void
  initialStep?: (typeof COMPANY_STEPS)[number]
}) {
  const { t } = useLingui()
  const lists = useLists()
  const customize = useAppStore((state) => state.store.customize)
  const navigate = useNavigate()
  const redirectAfterAuth = usePostLoginRedirect()
  const { mutateAsync: updateUser } = useMutation(trpc.auth.updateUser.mutationOptions())

  const [step, setStep] = useState(() =>
    props.initialStep ? Math.max(COMPANY_STEPS.indexOf(props.initialStep), 0) : 0,
  )

  // Change steps inside a view transition so the box morphs (fade out → border resize → fade in),
  // the same choreography used between routes and in the applicant wizard.
  const goToStep = (next: number | ((prev: number) => number)) => withViewTransition(() => setStep(next))

  // Step 0 — Company details
  const detailsForm = useAppForm({
    validationLogic: revalidateLogic(),
    validators: { onDynamic: companyDetailsSchema },
    defaultValues: {
      image: undefined as File | null | undefined,
      name: props.initialData?.name ?? props.user.name ?? '',
      employees: props.initialData?.employees ?? props.user.employees,
      sector: props.initialData?.sector ?? props.user.sector,
    },
    onSubmit: async ({ value }) => {
      // Company name is stored as the user's firstname (companies have no separate name column).
      await updateUser({ ...value, firstname: value.name, image: await fileToBase64(value.image ?? undefined) })
      goToStep(1)
    },
  })

  // Step 1 — Location
  const locationForm = useAppForm({
    validationLogic: revalidateLogic(),
    validators: { onDynamic: companyLocationSchema },
    defaultValues: {
      country: props.initialData?.country ?? props.user.country ?? '',
      province: props.initialData?.province ?? props.user.province ?? '',
      city: props.initialData?.city ?? props.user.city ?? '',
      cp: props.initialData?.cp ?? props.user.cp ?? '',
    },
    onSubmit: async ({ value }) => {
      await updateUser(value)
      goToStep(2)
    },
  })

  // Step 2 — Contact person (final submit)
  const contactForm = useAppForm({
    validationLogic: revalidateLogic(),
    validators: { onDynamic: contactInfoSchema },
    defaultValues: {
      contactFirstname: props.initialData?.contactFirstname ?? props.user.contactFirstname ?? '',
      contactLastname: props.initialData?.contactLastname ?? props.user.contactLastname ?? '',
      contactPhone: props.initialData?.contactPhone ?? props.user.contactPhone ?? '',
    },
    onSubmit: async ({ value }) => {
      const combined = {
        ...detailsForm.state.values,
        ...locationForm.state.values,
        ...value,
        firstname: detailsForm.state.values.name,
        image: await fileToBase64(detailsForm.state.values.image ?? undefined),
      }
      await updateUser(combined)
      props.callback?.({ action: 'registerCompany', applicantId: props.user.id })

      if (customize.applicantButtonExit) {
        window.location.href = `${customize.applicantButtonExit}?applicantId=${props.user.id}`
      } else {
        // Merge the values we just saved over the (stale) user prop, like the applicant wizard.
        redirectAfterAuth({ ...props.user, ...combined })
      }
    },
  })

  const stepTitles = [t`Tell us about your company`, t`Where are you based?`, t`Who should we contact?`]
  const stepSubtitles = [
    t`We'll use this to personalize your reports`,
    t`We will use this data for statistical analysis`,
    t`We'll reach out here if we ever need to`,
  ]

  const goBack = () => {
    if (step === 0) navigate({ to: '/register' })
    else goToStep((s) => s - 1)
  }

  return (
    <div className="mx-auto flex w-full max-w-[480px] flex-col items-center gap-6">
      <div className="flow-header text-center">
        <h1 className="text-2xl font-semibold">{stepTitles[step]}</h1>
        <p className="text-muted-foreground mt-1">{stepSubtitles[step]}</p>
      </div>

      {/* The border (flow-frame) resizes between steps while the content (flow-content) fades out →
          in, the same choreography as the applicant wizard. See app.css. */}
      <FlowFrame>
        <div className="flex flex-col gap-5">
          {step === 0 && (
            <form
              key="details"
              onSubmit={(event) => {
                event.preventDefault()
                event.stopPropagation()
                detailsForm.handleSubmit()
              }}
              className="flex flex-col gap-5"
            >
              <detailsForm.AppField
                name="image"
                children={(field) => (
                  <field.ImageField
                    label=""
                    variant="avatar"
                    uploadLabel={t`Company logo`}
                    url={props.initialData?.image ?? props.user.image ?? undefined}
                    enableCrop
                    autoOpenEditor={false}
                    optimizeOnUpload
                    showRemove
                    centerIcon={<HugeiconsIcon icon={CameraAddIcon} size={24} className="text-muted-foreground" />}
                    aspectRatio={{ width: 128, height: 128 }}
                  />
                )}
              />

              <detailsForm.AppField
                name="name"
                children={(field) => <field.TextField label={t`Company name`} autoComplete="organization" />}
              />
              <detailsForm.AppField
                name="employees"
                children={(field) => (
                  <field.SelectNumberField
                    label={t`Number of employees`}
                    options={lists.employees.map((label, value) => ({ value, label }))}
                  />
                )}
              />
              <detailsForm.AppField name="sector" children={(field) => <field.SectorField label={t`Sector`} />} />

              <div className="flex items-center justify-between gap-3">
                <Button type="button" variant="ghost" onClick={goBack}>
                  <ChevronLeftIcon />
                  {t`Back`}
                </Button>
                <detailsForm.AppForm>
                  <detailsForm.SubmitButton label={t`Continue to your location`}>
                    <ChevronRightIcon />
                  </detailsForm.SubmitButton>
                </detailsForm.AppForm>
              </div>
            </form>
          )}

          {step === 1 && (
            <form
              key="location"
              onSubmit={(event) => {
                event.preventDefault()
                event.stopPropagation()
                locationForm.handleSubmit()
              }}
              className="flex flex-col gap-5"
            >
              <locationForm.AppField
                name="country"
                listeners={{ onChange: () => locationForm.setFieldValue('province', '') }}
                children={(field) => <field.NativeCountryField label={t`Country`} />}
              />
              <locationForm.Subscribe selector={(state) => state.values.country}>
                {(country) => (
                  <locationForm.AppField
                    name="province"
                    children={(field) => <field.NativeProvinceField label={t`Province / State`} country={country} />}
                  />
                )}
              </locationForm.Subscribe>
              <locationForm.AppField
                name="city"
                children={(field) => <field.TextField label={t`City`} autoComplete="address-level2" />}
              />
              <locationForm.AppField
                name="cp"
                children={(field) => <field.TextField label={t`Postal code`} autoComplete="postal-code" />}
              />

              <div className="flex items-center justify-between gap-3">
                <Button type="button" variant="ghost" onClick={goBack}>
                  <ChevronLeftIcon />
                  {t`Back to your company`}
                </Button>
                <locationForm.AppForm>
                  <locationForm.SubmitButton label={t`Continue to contact`}>
                    <ChevronRightIcon />
                  </locationForm.SubmitButton>
                </locationForm.AppForm>
              </div>
            </form>
          )}

          {step === 2 && (
            <form
              key="contact"
              onSubmit={(event) => {
                event.preventDefault()
                event.stopPropagation()
                contactForm.handleSubmit()
              }}
              className="flex flex-col gap-5"
            >
              <contactForm.AppField
                name="contactFirstname"
                children={(field) => <field.TextField label={t`Contact first name`} />}
              />
              <contactForm.AppField
                name="contactLastname"
                children={(field) => <field.TextField label={t`Contact last name`} />}
              />
              <contactForm.AppField
                name="contactPhone"
                children={(field) => <field.TextField label={t`Contact phone`} />}
              />

              <div className="flex items-center justify-between gap-3">
                <Button type="button" variant="ghost" onClick={goBack}>
                  <ChevronLeftIcon />
                  {t`Back to your location`}
                </Button>
                <contactForm.AppForm>
                  <contactForm.SubmitButton label={t`Finish and go to your dashboard`}>
                    <CheckIcon />
                  </contactForm.SubmitButton>
                </contactForm.AppForm>
              </div>
            </form>
          )}
        </div>
      </FlowFrame>
    </div>
  )
}
